import React from 'react';
import { TRIGGER_TYPES, INDICATOR_TYPES, COMPARISON_OPERATORS } from '../../../config/api';
import { useSchema } from '../../../contexts/SchemaContext';

// SVG Icons
const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const TrendLineIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const IndicatorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
  </svg>
);

const CompareIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
  </svg>
);

const CandlestickIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 10V4a2 2 0 00-2-2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V10h-4zM6 10h4v4H6v-4z" />
  </svg>
);

const DragIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
  </svg>
);

/**
 * ConditionCard - Displays a single condition (indicator or trendline)
 * 
 * @param {Object} props
 * @param {Object} props.condition - Condition data
 * @param {number} props.index - Condition index
 * @param {Function} props.onDelete - Delete handler
 * @param {Function} props.onClick - Click handler for editing
 * @param {boolean} props.selected - Whether card is selected
 * @param {boolean} props.draggable - Whether card is draggable
 * @param {boolean} props.compact - Use compact display
 */
const ConditionCard = ({
  condition,
  index,
  onDelete,
  onClick,
  selected = false,
  draggable = false,
  compact = false,
}) => {
  const { conditionTypes } = useSchema();
  if (!condition) return null;

  const getOperatorLabel = (type, op) => {
    // Attempt to get label from schema specific to the condition type
    const ctInfo = conditionTypes?.[type];
    const schemaOp = ctInfo?.operators?.[op];
    if (schemaOp) return schemaOp.label || schemaOp.description;

    return COMPARISON_OPERATORS[op]?.label || op;
  };

  const { candlestickPatterns } = useSchema();

  const isIndicator = condition.conditionType === 'INDICATOR';
  const isTrendLine = condition.conditionType === 'TRENDLINE';
  const isComposite = condition.conditionType === 'COMPOSITE';
  const isCandlestick = condition.conditionType === 'CANDLESTICK';
  const isVote = condition.conditionType === 'VOTE';
  const isChain = condition.conditionType === 'CHAIN';

  // Get display values
  const getIndicatorDisplay = () => {
    const type = INDICATOR_TYPES[condition.indicatorType]?.label || condition.indicatorType;
    const operator = getOperatorLabel('INDICATOR', condition.operator);
    const params = condition.parameters || {};

    let paramStr = '';
    if (params.period) paramStr += `Period: ${params.period}`;
    if (params.threshold !== undefined && params.threshold !== null) {
      paramStr += paramStr ? ', ' : '';
      paramStr += `Threshold: ${params.threshold}`;
    }

    return { type, operator, paramStr };
  };

  const getTrendLineDisplay = () => {
    const trigger = TRIGGER_TYPES[condition.triggerType]?.label || condition.triggerType;
    const name = condition.trendLine?.name || 'Unnamed Trend Line';
    return { trigger, name };
  };

  const getCompositeDisplay = () => {
    const operator = getOperatorLabel('COMPOSITE', condition.operator);
    const leftType = INDICATOR_TYPES[condition.leftIndicator?.type || condition.leftIndicator?.indicatorType]?.label || (condition.leftIndicator?.type || condition.leftIndicator?.indicatorType);
    const rightType = INDICATOR_TYPES[condition.rightIndicator?.type || condition.rightIndicator?.indicatorType]?.label || (condition.rightIndicator?.type || condition.rightIndicator?.indicatorType);

    const leftParams = condition.leftIndicator?.parameters || {};
    const rightParams = condition.rightIndicator?.parameters || {};

    let leftStr = leftType;
    if (leftParams.period) leftStr += `(${leftParams.period})`;

    let rightStr = rightType;
    if (rightParams.period) rightStr += `(${rightParams.period})`;

    return { leftStr, operator, rightStr };
  };

  const getCandlestickDisplay = () => {
    const pattern = candlestickPatterns?.find(p => p.name === condition.patternType);
    return {
      name: pattern?.displayName || condition.patternType || 'Unknown Pattern',
      category: pattern?.category || 'Candlestick'
    };
  };

  const getVoteDisplay = () => {
    const total = condition.conditions?.length || 0;
    const required = condition.requiredVotes || 0;
    return { required, total };
  };

  const getChainDisplay = () => {
    const linkCount = condition.chainLinks?.length || 0;
    const initialType = condition.initialCondition?.conditionType || '?';
    return { initialType, linkCount };
  };

  // Compact display
  if (compact) {
    return (
      <span
        className={`badge gap-1 m-1 cursor-pointer ${isIndicator ? 'badge-primary' : isComposite ? 'badge-info' : isVote ? 'badge-warning' : isChain ? 'badge-accent' : 'badge-secondary'} ${selected ? '' : 'badge-outline'}`}
        onClick={onClick}
      >
        {isIndicator ? <IndicatorIcon /> : isComposite ? <CompareIcon /> : isCandlestick ? <CandlestickIcon /> : <TrendLineIcon />}
        {isIndicator
          ? `${condition.indicatorType} ${getOperatorLabel('INDICATOR', condition.operator)}`
          : isComposite
            ? `${condition.leftIndicator?.type} vs ${condition.rightIndicator?.type}`
            : isCandlestick
              ? `${getCandlestickDisplay().name}`
              : isVote
                ? `Vote ${getVoteDisplay().required}/${getVoteDisplay().total}`
                : isChain
                  ? `Chain (${getChainDisplay().linkCount + 1} steps)`
                  : `${condition.trendLine?.name || 'Trend Line'}`
        }
        {onDelete && (
          <button
            className="ml-1"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(index);
            }}
          >
            ×
          </button>
        )}
      </span>
    );
  }

  return (
    <div
      className={`card card-border bg-base-100 mb-2 ${onClick ? 'cursor-pointer hover:shadow-md hover:border-primary' : ''} ${selected ? 'border-primary border-2' : ''}`}
      onClick={onClick}
    >
      <div className="card-body p-3">
        <div className="flex items-start gap-2">
          {/* Drag handle */}
          {draggable && (
            <div className="cursor-grab text-base-content/30 mt-1">
              <DragIcon />
            </div>
          )}

          {/* Icon */}
          <div className={`p-2 rounded ${isIndicator ? 'bg-primary/10 text-primary' : isComposite ? 'bg-info/10 text-info' : isVote ? 'bg-warning/10 text-warning' : isChain ? 'bg-accent/10 text-accent' : 'bg-secondary/10 text-secondary'}`}>
            {isIndicator ? <IndicatorIcon /> : isComposite ? <CompareIcon /> : isCandlestick ? <CandlestickIcon /> : <TrendLineIcon />}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {isIndicator && (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm truncate">
                    {getIndicatorDisplay().type}
                  </span>
                  <span className="badge badge-outline badge-sm">
                    {getIndicatorDisplay().operator}
                  </span>
                </div>
                {getIndicatorDisplay().paramStr && (
                  <p className="text-xs text-base-content/60">
                    {getIndicatorDisplay().paramStr}
                  </p>
                )}
              </>
            )}

            {isTrendLine && (
              <>
                <span className="font-medium text-sm truncate block">
                  {getTrendLineDisplay().name}
                </span>
                <p className="text-xs text-base-content/60">
                  {getTrendLineDisplay().trigger}
                </p>
              </>
            )}

            {isComposite && (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">
                    {getCompositeDisplay().leftStr}
                  </span>
                  <span className="badge badge-info badge-sm badge-outline">
                    {getCompositeDisplay().operator}
                  </span>
                  <span className="font-medium text-sm">
                    {getCompositeDisplay().rightStr}
                  </span>
                </div>
                <p className="text-xs text-base-content/60">
                  Indicator Comparison
                </p>
              </>
            )}

            {isCandlestick && (
              <>
                <span className="font-medium text-sm truncate block">
                  {getCandlestickDisplay().name}
                </span>
                <p className="text-xs text-base-content/60">
                  {getCandlestickDisplay().category} Pattern
                </p>
              </>
            )}

            {isVote && (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">Vote Rule</span>
                  <span className="badge badge-warning badge-sm">
                    {getVoteDisplay().required} of {getVoteDisplay().total}
                  </span>
                </div>
                <p className="text-xs text-base-content/60">
                  At least {getVoteDisplay().required} of {getVoteDisplay().total} conditions must be true
                </p>
              </>
            )}

            {isChain && (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">Chain Rule</span>
                  <span className="badge badge-accent badge-sm">
                    {getChainDisplay().linkCount + 1} steps
                  </span>
                </div>
                <p className="text-xs text-base-content/60">
                  Sequential: {getChainDisplay().initialType} then {getChainDisplay().linkCount} link{getChainDisplay().linkCount !== 1 ? 's' : ''}
                </p>
              </>
            )}
          </div>

          {/* Delete button */}
          {onDelete && (
            <button
              className="btn btn-ghost btn-xs btn-circle text-error"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(index);
              }}
              title="Remove condition"
            >
              <DeleteIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConditionCard;
